import API_ENDPOINTS from '../config/apiConfig';
import { api } from './api';

// Simple in-memory cache (similar to laborBudgetCache)
const positionsCache = {
  cacheExpiry: 5 * 60 * 1000, // 5 minutes
  invalidateCache: () => {
    Object.keys(positionsCache).forEach((key) => {
      if (key !== 'invalidateCache' && key !== 'cacheExpiry') {
        delete positionsCache[key];
      }
    });

  },
};

// Build query string for filters (orgId, departmentId, includeDeleted)
const buildQuery = ({ orgId, departmentId, includeDeleted } = {}) => {
  const params = [];
  if (orgId) params.push(`orgId=${orgId}`);
  if (departmentId) params.push(`departmentId=${departmentId}`);
  if (includeDeleted) params.push('includeDeleted=1');
  return params.length > 0 ? `?${params.join('&')}` : '';
};

/** Drop every cached positions list (call after any write made outside this module). */
export const invalidatePositionsCache = () => positionsCache.invalidateCache();

export const getPositions = async (filters = {}) => {
  try {
    const url = `${API_ENDPOINTS.POSITIONS}${buildQuery(filters)}`;
    const cacheKey = JSON.stringify(filters);
    const now = Date.now();

    if (
      positionsCache[cacheKey] &&
      positionsCache[cacheKey].timestamp &&
      now - positionsCache[cacheKey].timestamp < positionsCache.cacheExpiry
    ) {

      return positionsCache[cacheKey].data;
    }

    const response = await api.get(url);
    const data = response.data || [];
    positionsCache[cacheKey] = { data, timestamp: now };
    return data;
  } catch (error) {
    console.error('Error in getPositions:', error);
    return [];
  }
};

export const createPosition = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.POSITIONS, data);
    positionsCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in createPosition:', error);
    throw error;
  }
};

/**
 * Partial update: send ONLY the fields that changed (plus id). An explicit
 * null clears a column; omitted keys keep their stored value.
 */
export const updatePosition = async (data) => {
  try {
    if (!data.id) throw new Error('Position ID is required');
    const response = await api.put(API_ENDPOINTS.POSITIONS, data);
    positionsCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in updatePosition:', error);
    throw error;
  }
};

export const deletePosition = async (id) => {
  try {
    if (!id) throw new Error('Position ID required');
    await api.delete(`${API_ENDPOINTS.POSITIONS}?id=${id}`);
    positionsCache.invalidateCache();
    return true;
  } catch (error) {
    console.error('Error in deletePosition:', error);
    throw error;
  }
};

/** Restore a soft-deleted position. */
export const restorePosition = async (id) => {
  try {
    if (!id) throw new Error('Position ID required');
    const response = await api.put(API_ENDPOINTS.POSITIONS, { id, isDeleted: false });
    positionsCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in restorePosition:', error);
    throw error;
  }
};

/**
 * Bulk update one field on many positions in a single statement.
 * field ∈ departmentId | typeClassificationId | revenueBasisGroupId | displayName
 * @returns {{ success: boolean, updated: number }}
 */
export const bulkUpdatePositions = async ({ orgId, ids, field, value }) => {
  if (!orgId) throw new Error('orgId is required');
  if (!Array.isArray(ids) || ids.length === 0) throw new Error('ids must be a non-empty array');
  if (!field) throw new Error('field is required');
  try {
    const response = await api.post(API_ENDPOINTS.POSITIONS, { action: 'bulkUpdate', orgId, ids, field, value });
    positionsCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in bulkUpdatePositions:', error);
    throw error;
  }
};
